//#region IMPORTS
import {
  instoreTemplatesSelectors,
  fetchInstoreTemplateAsync,
  instoreTemplateRowSelectors,
} from "../instoreTemplates/instoreTemplatesSlice";
import {
  storeFormatSelectors,
  fetchStoreFormatsAsync,
} from "../storeFormats/storeFormatSlice";
import {
  clearInstoreBasket,
  addInstoreBasketItem,
  setInstoreBasketStore,
} from "../instoreBasket/instoreBasketSlice";
import {
  SEARCH_STORE_MODAL,
  IMPERSONATE_STORE_FORMAT_MODAL,
} from "../../models/_consts";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { InstoreTemplateRow } from "../../models/instoreTemplate";
import { useTranslation } from "react-i18next";
import { openModal } from "../dashBoard/dashboardSlice";
import { Store } from "../../models/store";
import { toast } from "react-toastify";
import InstoreOrderSearchStoreForm from "../../components/instoreOrderDetail/InstoreOrderSearchStoreForm";
import WeekOrderItemsList from "../../components/weekorder/WeekOrderItemsList";
import usePickerManager from "../../hooks/usePickerManager";
import useAdminManager from "../../hooks/useAdminManager";
import MainContainer from "../../components/_layout/MainContainer";
import useBasketGate from "../../hooks/useBasketGate";
import useHQmanager from "../../hooks/useHQmanager";
import PageHeader from "../../components/genericPageHeader/PageHeader";
import MainModal from "../../components/_layout/MainModal";
//#endregion

export default function WeekOrderPage() {
  //#region SETUP
  const { t } = useTranslation();
  const { templateId } = useParams<{ templateId: string }>();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const template = useAppSelector((state) =>
    instoreTemplatesSelectors.selectById(state, templateId),
  );
  const allRows = useAppSelector(instoreTemplateRowSelectors.selectAll);
  const { lastLoadedTemplateId, loading } = useAppSelector(
    (state) => state.instoreTemplates,
  );
  const { user } = useAppSelector((state) => state.account);
  const { items: instoreBasketItems, store: impersonatedStore } =
    useAppSelector((state) => state.instoreBasket);
  const storeFormats = useAppSelector(storeFormatSelectors.selectAll);
  const { storeFormats_loaded } = useAppSelector((state) => state.storeFormats);

  // Roles
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);
  const isPicker = usePickerManager(user);
  // HQ and Picker impersonate
  const isStoreImpersonator = isHq || isPicker;
  const isReadOnly = isAdmin;

  // Admin impersonate
  const [impersonatedFormatId, setImpersonatedFormatId] = useState<
    number | null
  >(null);

  // Effective store format filter
  const effectiveStoreFormatId = isStoreImpersonator
    ? impersonatedStore?.store_format_id ?? null
    : isReadOnly
      ? impersonatedStore?.store_format_id ?? impersonatedFormatId
      : user?.store?.store_format_id ?? null;

  const rows = effectiveStoreFormatId
    ? allRows.filter((row) => row.store_format_id === effectiveStoreFormatId)
    : allRows;

  // Quantity per template_row_id currently in the instore basket
  const quantityByRowId = useMemo(() => {
    const map = new Map<number, number>();
    instoreBasketItems.forEach((item) =>
      map.set(item.template_row_id, item.quantity),
    );
    return map;
  }, [instoreBasketItems]);

  // Group rows by theme, sort by `index` within and between groups
  const groups = useMemo(() => {
    const map = new Map<
      number | string,
      { theme_id: number; theme: string; items: InstoreTemplateRow[] }
    >();
    for (const row of rows) {
      const key = row.theme_id ?? row.theme ?? `row_${row.id}`;
      if (!map.has(key)) {
        map.set(key, { theme_id: row.theme_id, theme: row.theme, items: [] });
      }
      map.get(key)!.items.push(row);
    }
    const list = Array.from(map.values());
    list.forEach((g) =>
      g.items.sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
    );
    list.sort((a, b) => (a.items[0]?.index ?? 0) - (b.items[0]?.index ?? 0));
    return list;
  }, [rows]);

  const { guardInstoreAction } = useBasketGate();

  // HQ and Picker order on behalf of an impersonated store; admin must also
  // pick a specific store. All three are blocked until a store is selected.
  const orderingDisabled =
    (isStoreImpersonator || isAdmin) && !impersonatedStore;
  //#endregion

  //#region LOGIC
  useEffect(() => {
    if (templateId && Number(templateId) !== lastLoadedTemplateId) {
      dispatch(fetchInstoreTemplateAsync(Number(templateId)));
    }
  }, [templateId, lastLoadedTemplateId, dispatch]);

  useEffect(() => {
    if (isReadOnly && !storeFormats_loaded) {
      dispatch(fetchStoreFormatsAsync());
    }
  }, [isReadOnly, storeFormats_loaded, dispatch]);

  useEffect(() => {
    if (
      isReadOnly &&
      !impersonatedFormatId &&
      !impersonatedStore &&
      storeFormats_loaded &&
      lastLoadedTemplateId === Number(templateId)
    ) {
      dispatch(openModal(IMPERSONATE_STORE_FORMAT_MODAL));
    }
  }, [
    isReadOnly,
    impersonatedFormatId,
    impersonatedStore,
    storeFormats_loaded,
    lastLoadedTemplateId,
    templateId,
    dispatch,
  ]);

  useEffect(() => {
    if (isStoreImpersonator && !impersonatedStore) {
      dispatch(openModal(SEARCH_STORE_MODAL));
    }
  }, [isStoreImpersonator, impersonatedStore, dispatch]);

  const addRowToBasket = guardInstoreAction(
    (row: InstoreTemplateRow, quantity: number) => {
      if (!template) return;
      dispatch(
        addInstoreBasketItem({ template_id: template.id, row, quantity }),
      );
    },
  );

  // Only formats actually present
  const templateFormatIds = useMemo(
    () => new Set(allRows.map((row) => row.store_format_id)),
    [allRows]
  );

  const availableStoreFormats = useMemo(
    () =>
      storeFormats
        .filter((sf) => templateFormatIds.has(sf.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [storeFormats, templateFormatIds]
  );

  // HQ store search modal handlers
  const openStoreSearch = () => {
    if (instoreBasketItems.length > 0) {
      toast.error(
        "Clear the basket before switching to another store."
      );
      return;
    }
    dispatch(openModal(SEARCH_STORE_MODAL));
  };

  const handleStorePicked = (store: Store) => {
    if (!store) {
      toast.error("No stores found");
      return;
    }
    if (!templateFormatIds.has(store.store_format_id)) {
      toast.error("This store's format is not part of this template.");
      return;
    }
    dispatch(setInstoreBasketStore(store));
    dispatch(openModal(false));
  };

  const clearImpersonation = () => {
    if (instoreBasketItems.length > 0) {
      toast.error("Clear the basket before clearing the impersonation.");
      return;
    }
    dispatch(setInstoreBasketStore(null));
  };

  const clearBasket = () => {
    dispatch(clearInstoreBasket());
    toast.success("Your instore basket is empty");
  };
  //#endregion

  //#region RENDER
  return (
    <MainContainer>
      <MainModal
        type={SEARCH_STORE_MODAL}
        closeDisabled={isStoreImpersonator && !impersonatedStore}
        closeHandler={() => dispatch(openModal(false))}
      >
        <InstoreOrderSearchStoreForm
          setStoreHandler={handleStorePicked}
          cancel={() => {
            dispatch(openModal(false));
            if (isStoreImpersonator && !impersonatedStore) {
              history.goBack();
            }
          }}
        />
      </MainModal>

      <PageHeader title={t("rituals.weekOrder") ?? "Week order"} />

      {template && (
        <div className="text-right">
          <p>
            <b>{template.name}</b>
          </p>
        </div>
      )}

      {/* HQ / Picker: store impersonation */}
      {isStoreImpersonator && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <div className="text-xl font-bold uppercase tracking-wide text-gray-800">
            {impersonatedStore?.store_format?.name}
          </div>
          <div className="flex items-center gap-3">
          {impersonatedStore ? (
            <div className="inline-flex items-center gap-2 rounded-md bg-purple-100 text-purple-900 px-3 py-1.5">
              <span className="font-medium">
                Ordering for {impersonatedStore.external_store_id} —{" "}
                {impersonatedStore.store_name}
              </span>
              <button
                type="button"
                onClick={clearImpersonation}
                disabled={instoreBasketItems.length > 0}
                className="text-purple-700 hover:text-purple-500 font-semibold disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-purple-700"
              >
                clear
              </button>
            </div>
          ) : (
            <p className="text-gray-500">No store selected</p>
          )}
          <button
            type="button"
            onClick={openStoreSearch}
            disabled={instoreBasketItems.length > 0}
            className="rounded-md bg-pink-700 text-white px-3 py-1.5 font-semibold hover:bg-pink-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-pink-700"
          >
            {impersonatedStore ? "Change store" : "Pick a store"}
          </button>
          {instoreBasketItems.length > 0 && (
            <button
              type="button"
              onClick={clearBasket}
              className="rounded-md bg-white text-red-700 ring-1 ring-inset ring-red-300 px-3 py-1.5 font-semibold hover:bg-red-50"
            >
              Clear basket
            </button>
          )}
          </div>
        </div>
      )}

      {/* Admin: format or store impersonation */}
      {isReadOnly && (
        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <div className="text-xl font-bold uppercase tracking-wide text-gray-800">
            {impersonatedStore?.store_format?.name ??
              availableStoreFormats.find(
                (sf) => sf.id === impersonatedFormatId,
              )?.name}
          </div>
          <div className="flex items-center gap-3">
          {effectiveStoreFormatId ? (
            <div className="inline-flex items-center gap-2 rounded-md bg-purple-100 text-purple-900 px-3 py-1.5">
              <span className="font-medium">
                {impersonatedStore
                  ? `Ordering for ${impersonatedStore.external_store_id} — ${
                      impersonatedStore.store_name
                    } (${
                      availableStoreFormats.find(
                        (sf) => sf.id === impersonatedStore.store_format_id
                      )?.name ?? "—"
                    })`
                  : `Impersonating format — ${
                      availableStoreFormats.find(
                        (sf) => sf.id === impersonatedFormatId
                      )?.name
                    }`}
              </span>
            </div>
          ) : (
            <p className="text-gray-500">No store format selected</p>
          )}
          <button
            type="button"
            onClick={() => {
              if (instoreBasketItems.length > 0) {
                toast.error(
                  "Clear the basket before switching impersonation."
                );
                return;
              }
              dispatch(openModal(IMPERSONATE_STORE_FORMAT_MODAL));
            }}
            disabled={instoreBasketItems.length > 0}
            className="rounded-md bg-pink-700 text-white px-3 py-1.5 font-semibold hover:bg-pink-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-pink-700"
          >
            {effectiveStoreFormatId ? "Change" : "Pick"}
          </button>
          {instoreBasketItems.length > 0 && (
            <button
              type="button"
              onClick={clearBasket}
              className="rounded-md bg-white text-red-700 ring-1 ring-inset ring-red-300 px-3 py-1.5 font-semibold hover:bg-red-50"
            >
              Clear basket
            </button>
          )}
          </div>
        </div>
      )}

      <MainModal
        type={IMPERSONATE_STORE_FORMAT_MODAL}
        closeDisabled={!effectiveStoreFormatId}
        closeHandler={() => dispatch(openModal(false))}
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Pick a store format or a store
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Pick a format to view its items, or pick a specific store to order
            on its behalf.
          </p>
          <ul className="mt-4 divide-y divide-gray-100">
            {availableStoreFormats.map((sf) => (
              <li key={sf.id}>
                <button
                  type="button"
                  onClick={() => {
                    setImpersonatedFormatId(sf.id);
                    dispatch(setInstoreBasketStore(null));
                    dispatch(openModal(false));
                  }}
                  className={`w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50 ${
                    impersonatedFormatId === sf.id && !impersonatedStore
                      ? "font-semibold text-purple-700"
                      : "text-gray-900"
                  }`}
                >
                  <span>{sf.name}</span>
                  {impersonatedFormatId === sf.id && !impersonatedStore && (
                    <span className="text-xs text-purple-600">selected</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-6 border-t pt-4">
            <InstoreOrderSearchStoreForm
              setStoreHandler={(store) => {
                if (!store) {
                  toast.error("No store found");
                  return;
                }
                if (!templateFormatIds.has(store.store_format_id)) {
                  toast.error(
                    "This store's format is not part of this template."
                  );
                  return;
                }
                setImpersonatedFormatId(store.store_format_id);
                dispatch(setInstoreBasketStore(store));
                dispatch(openModal(false));
              }}
              cancel={() => dispatch(openModal(false))}
            />
          </div>
        </div>
      </MainModal>

      <WeekOrderItemsList
        groups={groups}
        quantityByRowId={quantityByRowId}
        onRowAdd={addRowToBasket}
        loading={loading}
        disabled={orderingDisabled}
        showStoreFormat={isAdmin}
      />
    </MainContainer>
  );
  //#endregion
}
